import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

const Notifications = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all'); // all, unread, bookings, messages, reviews

    useEffect(() => {
        if (!user) {
            navigate('/sign-in-page');
            return;
        }
        loadNotifications();
    }, [user, navigate]);

    const loadNotifications = async () => {
        setLoading(true);
        try {
            // In production, fetch from notifications table
            // For now, create mock notifications based on user activity
            const mockNotifications = [
                {
                    id: 1,
                    type: 'booking',
                    title: 'Booking Confirmed',
                    message: 'Your booking with Elegant Affairs Photography has been confirmed for March 15, 2024',
                    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
                    read: false,
                    icon: 'CheckCircle',
                    iconColor: '#10b981',
                    actionLabel: 'View Booking',
                    actionLink: '/user-dashboard'
                },
                {
                    id: 2,
                    type: 'message',
                    title: 'New Message',
                    message: 'Bollywood Beats DJ responded to your inquiry',
                    timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
                    read: false,
                    icon: 'MessageCircle',
                    iconColor: '#3b82f6',
                    actionLabel: 'Read Message',
                    actionLink: '/user-dashboard'
                },
                {
                    id: 3,
                    type: 'review',
                    title: 'Review Reminder',
                    message: 'How was your experience with Mehndi Magic Artists? Share your feedback',
                    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
                    read: false,
                    icon: 'Star',
                    iconColor: '#f59e0b',
                    actionLabel: 'Write Review',
                    actionLink: '/user-dashboard'
                },
                {
                    id: 4,
                    type: 'promotion',
                    title: 'Special Offer',
                    message: 'Save 15% on bookings made this week! Limited time offer',
                    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
                    read: true,
                    icon: 'Gift',
                    iconColor: '#8b5cf6',
                    actionLabel: 'View Offers',
                    actionLink: '/category-browse'
                },
                {
                    id: 5,
                    type: 'system',
                    title: 'Profile Update',
                    message: 'Your wedding date is approaching! Make sure all your bookings are confirmed',
                    timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
                    read: true,
                    icon: 'Calendar',
                    iconColor: '#6b5b4f',
                    actionLabel: 'Check Dashboard',
                    actionLink: '/user-dashboard'
                },
                {
                    id: 6,
                    type: 'booking',
                    title: 'Payment Reminder',
                    message: 'Payment due for Raj Palace Caterers - $500 remaining balance',
                    timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
                    read: true,
                    icon: 'DollarSign',
                    iconColor: '#ef4444',
                    actionLabel: 'Make Payment',
                    actionLink: '/user-dashboard'
                }
            ];

            setNotifications(mockNotifications);
        } catch (error) {
            console.error('Error loading notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (notificationId) => {
        setNotifications(prev =>
            prev.map(notif =>
                notif.id === notificationId ? { ...notif, read: true } : notif
            )
        );
        // In production: await supabase.from('notifications').update({ read: true }).eq('id', notificationId);
    };

    const markAllAsRead = async () => {
        setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
        // In production: await supabase.from('notifications').update({ read: true }).eq('user_id', user.id);
    };

    const deleteNotification = async (notificationId) => {
        setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
        // In production: await supabase.from('notifications').delete().eq('id', notificationId);
    };

    const handleNotificationClick = (notification) => {
        markAsRead(notification.id);
        if (notification.actionLink) {
            navigate(notification.actionLink);
        }
    };

    const getFilteredNotifications = () => {
        let filtered = notifications;

        if (filter === 'unread') {
            filtered = filtered.filter(n => !n.read);
        } else if (filter !== 'all') {
            filtered = filtered.filter(n => n.type === filter);
        }

        return filtered;
    };

    const filteredNotifications = getFilteredNotifications();
    const unreadCount = notifications.filter(n => !n.read).length;

    const getTimeAgo = (timestamp) => {
        const now = new Date();
        const notifTime = new Date(timestamp);
        const diffMs = now - notifTime;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffMins < 60) return `${diffMins} minutes ago`;
        if (diffHours < 24) return `${diffHours} hours ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        return notifTime.toLocaleDateString();
    };

    const filterOptions = [
        { value: 'all', label: 'All', icon: 'Bell' },
        { value: 'unread', label: 'Unread', icon: 'BellDot', count: unreadCount },
        { value: 'bookings', label: 'Bookings', icon: 'Calendar' },
        { value: 'messages', label: 'Messages', icon: 'MessageCircle' },
        { value: 'reviews', label: 'Reviews', icon: 'Star' }
    ];

    return (
        <div className="min-h-screen bg-background">
            <Header />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="font-heading font-bold text-3xl text-text-primary">
                                Notifications
                            </h1>
                            <p className="text-text-secondary mt-2">
                                Stay updated with your bookings, messages, and wedding planning progress
                            </p>
                        </div>

                        {unreadCount > 0 && (
                            <Button
                                variant="outline"
                                size="sm"
                                iconName="CheckCheck"
                                onClick={markAllAsRead}
                            >
                                Mark All as Read
                            </Button>
                        )}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white rounded-xl p-4 border border-border">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                                    <Icon name="Bell" size={20} color="#8B4513" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-text-primary">{notifications.length}</p>
                                    <p className="text-sm text-text-secondary">Total</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl p-4 border border-border">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                                    <Icon name="BellDot" size={20} color="#3b82f6" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-text-primary">{unreadCount}</p>
                                    <p className="text-sm text-text-secondary">Unread</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl p-4 border border-border">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                                    <Icon name="Calendar" size={20} color="#10b981" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-text-primary">
                                        {notifications.filter(n => n.type === 'booking').length}
                                    </p>
                                    <p className="text-sm text-text-secondary">Bookings</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl p-4 border border-border">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                                    <Icon name="MessageCircle" size={20} color="#8b5cf6" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-text-primary">
                                        {notifications.filter(n => n.type === 'message').length}
                                    </p>
                                    <p className="text-sm text-text-secondary">Messages</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl border border-border mb-6 p-4">
                    <div className="flex flex-wrap gap-2">
                        {filterOptions.map(option => (
                            <button
                                key={option.value}
                                onClick={() => setFilter(option.value)}
                                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${filter === option.value
                                        ? 'bg-primary text-white'
                                        : 'bg-surface-100 text-text-secondary hover:bg-surface-200'
                                    }`}
                            >
                                <Icon name={option.icon} size={16} color="currentColor" />
                                <span>{option.label}</span>
                                {option.count > 0 && (
                                    <span className="ml-1 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                                        {option.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Notifications List */}
                {loading ? (
                    <div className="bg-white rounded-xl border border-border p-12 text-center">
                        <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                        <p className="text-text-secondary">Loading notifications...</p>
                    </div>
                ) : filteredNotifications.length === 0 ? (
                    <div className="bg-white rounded-xl border border-border p-12 text-center">
                        <div className="w-16 h-16 bg-surface-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Icon name="Bell" size={32} color="#9ca3af" />
                        </div>
                        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                            No notifications
                        </h3>
                        <p className="text-text-secondary">
                            {filter === 'unread'
                                ? "You're all caught up! No unread notifications."
                                : "You don't have any notifications yet."}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredNotifications.map(notification => (
                            <div
                                key={notification.id}
                                className={`bg-white rounded-xl border border-border p-4 hover:shadow-soft transition-shadow ${!notification.read ? 'border-l-4 border-l-primary' : ''
                                    }`}
                            >
                                <div className="flex items-start space-x-4">
                                    {/* Icon */}
                                    <div
                                        className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                                        style={{ backgroundColor: `${notification.iconColor}20` }}
                                    >
                                        <Icon name={notification.icon} size={24} color={notification.iconColor} />
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex items-center space-x-2">
                                                <h3 className="font-heading font-semibold text-text-primary">
                                                    {notification.title}
                                                </h3>
                                                {!notification.read && (
                                                    <span className="w-2 h-2 bg-primary rounded-full"></span>
                                                )}
                                            </div>
                                            <button
                                                onClick={() => deleteNotification(notification.id)}
                                                className="text-text-muted hover:text-text-secondary transition-colors"
                                            >
                                                <Icon name="X" size={16} color="currentColor" />
                                            </button>
                                        </div>

                                        <p className="text-text-secondary mb-3">{notification.message}</p>

                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-text-muted">
                                                {getTimeAgo(notification.timestamp)}
                                            </span>

                                            <div className="flex items-center space-x-3">
                                                {!notification.read && (
                                                    <button
                                                        onClick={() => markAsRead(notification.id)}
                                                        className="text-sm text-primary hover:text-primary-600 font-medium"
                                                    >
                                                        Mark as read
                                                    </button>
                                                )}
                                                {notification.actionLabel && (
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleNotificationClick(notification)}
                                                    >
                                                        {notification.actionLabel}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
};

export default Notifications;
